import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { FaShopify } from 'react-icons/fa';
import styled from 'styled-components';

import { BadgeWithIcon, Button, RamblaActionButtonContent, Spinner } from 'components';
import { StyledTooltip } from 'components/InfoTooltip/InfoTooltip';
import { PermissionContext } from 'components/common/ProtectedRoute';
import entities from 'constants/entities';
import EntityForm from 'containers/EntityForm/entityForm';
import * as Ui from './createEntityModal.styles';

const ShopifyButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  color: white;
  background-color: #96bf48;
  border-radius: 50%;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #7da639;
  }
`;

const WarningText = styled.span`
  color: #e63758;
  display: block;
  min-height: 21px;
`;

const SubTitleText = styled.span`
  display: block;
  min-height: 21px;
`;

const ShopifyBadgeText = styled.span`
  text-transform: none;
  font-size: 16px;
  font-style: italic;
`;

const productModalContentStyle = {
  width: 'min(960px, calc(100vw - 32px))',
};

const childProductModalContentStyle = {
  width: 'min(1080px, calc(100vw - 32px))',
};

const CreateEntityModal = ({
  title,
  subTitle,
  entity,
  trigger = 'button',
  triggerCopy,
  renderSubButton,
  wide = false,
  isShopifyIntegration = false,
  isLoadingIntegration = false,
  ...rest
}) => {
  const { t } = useTranslation(['pages', 'app']);
  const { permission } = React.useContext(PermissionContext);
  const [missingItems, setMissingItems] = useState(null);

  const isParentProduct = entity === 'parent_product';
  const isChildProduct = entity === 'child_product';

  const isDataLoaded = missingItems !== null;
  const showWarning = missingItems && missingItems.length > 0;

  const handleRequiredDataLoad = useCallback((items) => {
    setMissingItems(items);
  }, []);

  const effectiveSubTitle = (() => {
    if (!showWarning) return subTitle;

    if (isParentProduct) {
      return t('pages:parentProducts.noCategoriesWarning');
    }

    if (isChildProduct) {
      const missingLabels = missingItems.map((item) => {
        switch (item) {
          case 'parentProduct':
            return t('pages:childProducts.parentProduct');
          case 'inventory':
            return t('pages:childProducts.inventory');
          case 'color':
            return t('pages:childProducts.color');
          case 'size':
            return t('pages:childProducts.size');
          default:
            return item;
        }
      });

      return `${t('pages:childProducts.missingRequiredData')}: ${missingLabels.join(', ')}`;
    }

    return subTitle;
  })();

  const getHeader = () => {
    if (!isParentProduct && !isChildProduct) {
      return { title, subTitle };
    }

    if (!isDataLoaded) {
      return {
        title,
        subTitle: <Spinner style={{ width: 20, height: 20 }} />,
      };
    }

    const subtitle = showWarning ? (
      <WarningText>{effectiveSubTitle}</WarningText>
    ) : (
      <SubTitleText>{effectiveSubTitle}</SubTitleText>
    );

    return { title, subTitle: subtitle };
  };

  const generateTrigger = () => {
    if (typeof trigger === 'object') {
      return trigger;
    }
    if (trigger === 'button') {
      return (
        <Button variant="primary" style={{ minWidth: 130 }}>
          <RamblaActionButtonContent icon="addModule">
            {triggerCopy || t('app:createNew')}
          </RamblaActionButtonContent>
        </Button>
      );
    }
    return (
      <button type="button" className="btn btn-link p-0 btn-sm">
        {t('createProduct.addNew')}
      </button>
    );
  };

  if (!permission?.create) {
    return null;
  }

  if (isLoadingIntegration) {
    return <Spinner style={{ width: 20, height: 20 }} />;
  }

  if (isShopifyIntegration) {
    return (
      <ShopifyButtonContainer>
        <StyledTooltip
          title={t('pages:parentProducts.shopifyIntegrationTooltip')}
          placement="top"
          arrow
        >
          <InfoIconWrapper>
            <AiOutlineInfoCircle size={24} color="white" />
          </InfoIconWrapper>
        </StyledTooltip>
        <BadgeWithIcon
          Icon={<FaShopify color="white" size={24} />}
          label="shopify"
          iconSize={36}
          backgroundColor="#96bf48"
          color="white"
          boxShadowSize={[
            { size: 2, color: 'white' },
            { size: 3, color: '#96bf48' },
          ]}
          imageBoxShadowSize={[
            { size: 2, color: 'white' },
            { size: 3, color: '#96bf48' },
          ]}
        >
          <ShopifyBadgeText>shopify</ShopifyBadgeText>
        </BadgeWithIcon>
      </ShopifyButtonContainer>
    );
  }

  return (
    <Ui.Modal
      className="modal-lg"
      contentStyle={
        isChildProduct
          ? childProductModalContentStyle
          : isParentProduct
            ? productModalContentStyle
            : undefined
      }
      wide={wide}
      trigger={generateTrigger()}
      header={getHeader()}
      renderSubButton={renderSubButton}
    >
      <EntityForm
        entity={entity}
        type="create"
        onRequiredDataLoad={handleRequiredDataLoad}
        {...rest}
      />
    </Ui.Modal>
  );
};

CreateEntityModal.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  renderSubButton: PropTypes.func,
  entity: PropTypes.oneOf(Object.keys(entities)),
  trigger: PropTypes.oneOf(['button', 'link']),
  wide: PropTypes.bool,
  triggerCopy: PropTypes.string,
  isShopifyIntegration: PropTypes.bool,
  isLoadingIntegration: PropTypes.bool,
};

export default CreateEntityModal;
